import React, { useEffect, useState } from "react";
import { 
  Search, 
  RotateCw, 
  SlidersHorizontal, 
  MoreVertical, 
  ChevronDown,
  FileSpreadsheet,
  Eraser,
  Building2,
  BookOpen,
  GraduationCap,
  CalendarDays,
  Trophy,
  UploadCloud,
  Download,
  FileText,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { getAllClasses } from "../../../api/elearning/classes/classes";
import { getAllBranches } from "../../../api/hrm/branches/info";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const ResultFilter = ({
  onFilterChange,
  onExportClick,
  onImportClick,
  onRefreshClick,
  defaultBranchId,
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [courses, setCourses] = useState([]);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState(() => ({
    keyword: "",
    branch_id: defaultBranchId ? String(defaultBranchId) : "",
    course_id: "",
    class_id: "",
    exam_type: "",
    date_range: "",
  }));

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      branch_id: defaultBranchId ? String(defaultBranchId) : "",
    }));
  }, [defaultBranchId]);

  const examTypeOptions = [
    { value: "Entrance", label: "Đầu vào" },
    { value: "Quiz", label: "Kiểm tra" },
    { value: "Mid-term", label: "Giữa kỳ" },
    { value: "Final", label: "Cuối kỳ" },
  ];

  const isFiltering = ["keyword", "course_id", "class_id", "exam_type", "date_range"].some(
    (k) => filters[k] !== ""
  );

  // ✅ Responsive detector
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Load dữ liệu bổ trợ cho Filter
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, classRes, branchRes] = await Promise.all([
          getAllCoursesNoPaging(),
          getAllClasses({ page: 1, size: 500, is_active: 1 }),
          getAllBranches()
        ]);
        setCourses(courseRes.records || courseRes || []);
        setClasses(classRes.records || []);
        const branchItems = branchRes?.items || branchRes || [];
        setBranches(Array.isArray(branchItems) ? branchItems : []);
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu lọc:", error);
      }
    };
    fetchData();
  }, []);

  // ✅ Debounce keyword (400ms tương tự StudentFilter)
  useEffect(() => {
    const timer = setTimeout(() => onFilterChange?.(filters), 400);
    return () => clearTimeout(timer);
  }, [filters.keyword]);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      branch_id: filters.branch_id || (defaultBranchId ? String(defaultBranchId) : ""),
      course_id: "",
      class_id: "",
      exam_type: "",
      date_range: "",
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const downloadFullTemplate = () => {
    const base = process.env.PUBLIC_URL || "";
    const a = document.createElement("a");
    a.href = `${base}/templates/result_template.csv`;
    a.download = "mau_bang_diem_day_du.csv";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const openResultsGuide = () => {
    const base = process.env.PUBLIC_URL || "";
    window.open(`${base}/guides/elearning-results-guide.html`, "_blank", "noopener,noreferrer");
  };

  // ================= RENDER COMPONENTS (Đồng bộ StudentFilter) =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active 
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm" 
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, icon: Icon }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors" />
      </div>
    </div>
  );

  // ================= MOBILE UI =================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[140px] group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm tên học viên..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all outline-none"
            />
          </div>
          <button
            type="button"
            onClick={onImportClick}
            className="flex items-center gap-2 px-4 py-3 bg-red-600 text-white rounded-2xl font-bold text-xs uppercase shrink-0 shadow-md shadow-red-100"
          >
            <UploadCloud size={16} />
            Nhập điểm
          </button>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} />
          <div className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen(!moreOpen)}
              className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all ${moreOpen ? "bg-gray-100 border-gray-300" : "bg-white border-gray-200 text-gray-500"}`}
            >
              <MoreVertical size={18} />
            </button>
            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-2 w-56 bg-white border border-gray-100 rounded-[1.5rem] shadow-2xl z-50 p-2 overflow-hidden"
                >
                  <button
                    type="button"
                    className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    onClick={() => {
                      setMoreOpen(false);
                      onExportClick?.();
                    }}
                  >
                    <Download size={16} />
                    XUẤT CSV
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    onClick={() => {
                      setMoreOpen(false);
                      downloadFullTemplate();
                    }}
                  >
                    <FileSpreadsheet size={16} />
                    TẢI BIỂU MẪU
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    onClick={() => {
                      setMoreOpen(false);
                      openResultsGuide();
                    }}
                  >
                    <FileText size={16} />
                    HƯỚNG DẪN
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc kết quả"
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200 uppercase tracking-wider"
            >
              ÁP DỤNG
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox label="Chi nhánh" value={filters.branch_id} options={branches.map(b => ({value: b.id, label: b.name}))} onChange={(v) => updateFilter("branch_id", v)} icon={Building2} />
            <SelectBox label="Khóa học" value={filters.course_id} options={courses.map(c => ({value: c.id, label: c.name}))} onChange={(v) => updateFilter("course_id", v)} icon={BookOpen} />
            <SelectBox label="Lớp học" value={filters.class_id} options={classes.filter(c => !filters.course_id || c.course_id === filters.course_id).map(c => ({value: c.id, label: c.name}))} onChange={(v) => updateFilter("class_id", v)} icon={GraduationCap} />
            <SelectBox label="Loại điểm" value={filters.exam_type} options={examTypeOptions} onChange={(v) => updateFilter("exam_type", v)} icon={Trophy} />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ================= DESKTOP UI =================
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6 text-left relative">
      <div className="flex items-center justify-between gap-4">
        {/* Nhóm tìm kiếm & nút chức năng */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm theo tên học viên..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={() => onRefreshClick?.(filters)}
            title="Làm mới"
          />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />
          
          {isFiltering && (
             <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
                <Eraser size={14} /> Xóa lọc
             </button>
          )}
        </div>

        {/* Nhập điểm nổi bật; còn lại trong More */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onImportClick}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-full font-bold hover:bg-red-700 shadow-lg shadow-red-100 transition-all active:scale-95 uppercase text-xs"
          >
            <UploadCloud size={18} />
            Nhập điểm
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen(!moreOpen)}
              className={`h-11 px-3 flex items-center justify-center gap-1.5 rounded-xl border transition-all text-xs font-bold uppercase tracking-wider ${
                moreOpen ? "bg-gray-100 border-gray-300 text-gray-800" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              title="Thêm tùy chọn"
            >
              <MoreVertical size={18} />
              <span className="hidden sm:inline">Thêm</span>
            </button>

            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-3 w-56 bg-white border border-gray-100 rounded-[1.5rem] shadow-2xl z-50 p-2 overflow-hidden"
                  onMouseLeave={() => setMoreOpen(false)}
                >
                  <button
                    type="button"
                    className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    onClick={() => {
                      setMoreOpen(false);
                      onExportClick?.();
                    }}
                  >
                    <Download size={16} />
                    XUẤT CSV
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    onClick={() => {
                      setMoreOpen(false);
                      downloadFullTemplate();
                    }}
                  >
                    <FileSpreadsheet size={16} />
                    TẢI BIỂU MẪU
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                    onClick={() => {
                      setMoreOpen(false);
                      openResultsGuide();
                    }}
                  >
                    <FileText size={16} />
                    HƯỚNG DẪN NHẬP LIỆU
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Vùng lọc nâng cao */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox label="Chi nhánh" value={filters.branch_id} options={branches.map(b => ({value: b.id, label: b.name}))} onChange={(v) => updateFilter("branch_id", v)} icon={Building2} />
              <SelectBox label="Khóa học" value={filters.course_id} options={courses.map(c => ({value: c.id, label: c.name}))} onChange={(v) => updateFilter("course_id", v)} icon={BookOpen} />
              <SelectBox label="Lớp học" value={filters.class_id} options={classes.filter(c => !filters.course_id || c.course_id === filters.course_id).map(c => ({value: c.id, label: c.name}))} onChange={(v) => updateFilter("class_id", v)} icon={GraduationCap} />
              <SelectBox label="Loại điểm" value={filters.exam_type} options={examTypeOptions} onChange={(v) => updateFilter("exam_type", v)} icon={Trophy} />
              <div className="flex flex-col gap-1.5 w-full">
                <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">
                  <CalendarDays size={12} /> Khoảng ngày
                </label>
                <input 
                  type="date"
                  value={filters.date_range}
                  onChange={(e) => updateFilter("date_range", e.target.value)}
                  className="w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 rounded-2xl text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all shadow-sm font-medium"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ResultFilter;